import { useMemo, useState } from 'react';

import {
  useCreateStepTemplate,
  useDeleteStepTemplate,
  useDuplicateStepTemplate,
  useStepTemplates,
  useUpdateStepTemplate,
} from '../../hooks/useOutreach';
import type {
  StepTemplate,
  StepTemplateCompat,
  StepTemplateIn,
  StepTemplateUpdate,
} from '../../types/outreach';
import { formatEt } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { EmailBodyPreview, RichEmailEditor } from './RichEmailEditor';


// Color tokens for the compat badge. Matches the spec doc:
//   per_person       -> primary blue (most-used class)
//   per_organization -> warm orange
//   both             -> muted green ("any mode")
const COMPAT_COLORS: Record<StepTemplateCompat, { bg: string; fg: string; label: string }> = {
  per_person: { bg: '#E6F0FA', fg: '#1A4F8A', label: 'Per person' },
  per_organization: { bg: '#FBEBD8', fg: '#7B5400', label: 'Per org' },
  both: { bg: '#E6F4EA', fg: '#2E6E2E', label: 'Any mode' },
};


function CompatBadge({ compat }: { compat: StepTemplateCompat }) {
  const c = COMPAT_COLORS[compat];
  return (
    <span
      style={{
        background: c.bg, color: c.fg,
        padding: '2px 8px', borderRadius: 12,
        fontSize: 11, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {c.label}
    </span>
  );
}


/**
 * Top-level Outreach > Templates tab.
 *
 * Templates store reusable step subject + body pairs. Authors save a
 * step from the campaign editor with "Save as template", and load one
 * back into another step with "Load template". This page is the
 * library view: list + edit + archive + delete.
 *
 * Two panels via a tab toggle: Active vs Archived. Hard delete is only
 * possible from the Archived view (backend gate enforces this too).
 */
export function TemplatesAdmin() {
  const [view, setView] = useState<'active' | 'archived'>('active');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const templatesQ = useStepTemplates({
    archived: view === 'archived',
    search: search.trim() || undefined,
  });

  if (creating) {
    return <TemplateEditor onClose={() => setCreating(false)} />;
  }
  if (editingId != null) {
    return (
      <TemplateEditor
        templateId={editingId}
        onClose={() => setEditingId(null)}
      />
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 0 }}>
          <button
            type="button"
            className={`${styles.button} ${view === 'active' ? styles.buttonPrimary : ''}`}
            onClick={() => setView('active')}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          >
            Active
          </button>
          <button
            type="button"
            className={`${styles.button} ${view === 'archived' ? styles.buttonPrimary : ''}`}
            onClick={() => setView('archived')}
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: -1 }}
          >
            Archived
          </button>
        </div>
        <input
          className={styles.input}
          type="text"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 320 }}
        />
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={() => setCreating(true)}
        >
          + New template
        </button>
      </div>

      {templatesQ.isLoading && <div className={styles.empty}>Loading…</div>}
      {templatesQ.isError && (
        <div className={styles.error}>
          Failed to load templates: {String(templatesQ.error)}
        </div>
      )}
      {templatesQ.data && templatesQ.data.length === 0 && (
        <div className={styles.empty}>
          {view === 'active'
            ? 'No templates yet. Click "+ New template" or "Save as template" from a step editor.'
            : 'No archived templates.'}
        </div>
      )}

      {templatesQ.data && templatesQ.data.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Mode</th>
              <th>Variables</th>
              <th>Last edited</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templatesQ.data.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                view={view}
                onEdit={() => setEditingId(t.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function TemplateRow({
  template, view, onEdit,
}: {
  template: StepTemplate;
  view: 'active' | 'archived';
  onEdit: () => void;
}) {
  const updateMut = useUpdateStepTemplate();
  const deleteMut = useDeleteStepTemplate();
  const duplicateMut = useDuplicateStepTemplate();

  async function handleArchive() {
    await updateMut.mutateAsync({
      id: template.id,
      body: { archived: view !== 'archived' },
    });
  }

  async function handleDuplicate() {
    try {
      await duplicateMut.mutateAsync(template.id);
    } catch (e: unknown) {
      alert(`Duplicate failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleDelete() {
    if (!window.confirm(
      `Permanently delete '${template.name}'? This can't be undone.`,
    )) return;
    await deleteMut.mutateAsync(template.id);
  }

  // Render a short text-only preview of the body. Strip tags for the
  // table row -- full preview lives in the editor.
  const preview = useMemo(() => {
    const text = template.body_html_template
      .replace(/<[^>]*>/g, ' ')
      .replace(/&[a-z]+;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > 60 ? text.slice(0, 60) + '…' : text;
  }, [template.body_html_template]);

  return (
    <tr>
      <td onClick={onEdit} style={{ cursor: 'pointer' }}>
        <div style={{ fontWeight: 500 }}>{template.name}</div>
        <div className={styles.helpText} style={{ marginTop: 2 }}>
          {preview || <em>(empty)</em>}
        </div>
      </td>
      <td><CompatBadge compat={template.send_mode_compat} /></td>
      <td>
        <span className={styles.helpText} style={{ fontSize: 11 }}>
          {template.variables_used.length === 0
            ? '—'
            : template.variables_used.join(', ')}
        </span>
      </td>
      <td>
        <span className={styles.helpText}>
          {formatEt(template.updated_at)}
        </span>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            className={styles.button}
            onClick={onEdit}
          >
            Edit
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={handleDuplicate}
            disabled={duplicateMut.isPending}
            title="Clone this template under a new name (auto-suffixed '(copy)')."
          >
            {duplicateMut.isPending ? 'Duplicating…' : 'Duplicate'}
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={handleArchive}
            disabled={updateMut.isPending}
          >
            {view === 'archived' ? 'Restore' : 'Archive'}
          </button>
          {view === 'archived' && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={handleDelete}
              disabled={deleteMut.isPending}
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}


/**
 * Create or edit a single template. Passing `templateId` loads it via
 * useStepTemplate; omitting it starts a blank form.
 *
 * Note on RichEmailEditor's sendMode: we pass 'per_person' because
 * that's the merge-field set most authors will use, but the server's
 * compat detector handles {{FirstNamesAlpha}} etc. correctly even if
 * typed manually. (The merge-menu doesn't constrain saving.)
 */
function TemplateEditor({
  templateId,
  onClose,
}: {
  templateId?: number;
  onClose: () => void;
}) {
  const isNew = templateId == null;
  // For edit mode, we cheat and reuse the list query's cached row so we
  // don't need an extra useStepTemplate fetch. List query is invalidated
  // on mutation so the cache is fresh.
  const listQ = useStepTemplates();
  const initial = useMemo<StepTemplate | undefined>(
    () =>
      templateId != null
        ? listQ.data?.find((t) => t.id === templateId)
        : undefined,
    [listQ.data, templateId],
  );

  const [name, setName] = useState(initial?.name ?? '');
  const [hasSubject, setHasSubject] = useState(
    Boolean(initial?.subject_template),
  );
  const [subject, setSubject] = useState(initial?.subject_template ?? '');
  const [bodyHtml, setBodyHtml] = useState(initial?.body_html_template ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const createMut = useCreateStepTemplate();
  const updateMut = useUpdateStepTemplate();
  const busy = createMut.isPending || updateMut.isPending;

  async function handleSave() {
    setError(null);
    if (!name.trim()) return setError('Name is required.');
    if (!bodyHtml.trim()) return setError('Body is required.');

    const subjectVal = hasSubject ? subject.trim() : '';

    try {
      if (isNew) {
        const body: StepTemplateIn = {
          name: name.trim(),
          subject_template: subjectVal || null,
          body_html_template: bodyHtml,
          notes: notes.trim() || null,
        };
        await createMut.mutateAsync(body);
      } else {
        const body: StepTemplateUpdate = {
          name: name.trim(),
          subject_template: subjectVal || null,
          body_html_template: bodyHtml,
          notes: notes.trim() || null,
        };
        await updateMut.mutateAsync({ id: templateId!, body });
      }
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  // While the list query loads in edit mode, show a brief placeholder so
  // we don't render an editor with empty initial state and silently
  // overwrite the user's saved template on first save.
  if (!isNew && !initial) {
    if (listQ.isLoading) {
      return <div className={styles.empty}>Loading template…</div>;
    }
    return (
      <div className={styles.error}>
        Template not found. <button onClick={onClose}>Back</button>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0, fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
          {isNew ? 'New template' : `Edit '${initial?.name ?? ''}'`}
        </h3>
        {initial && (
          <CompatBadge compat={initial.send_mode_compat} />
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.formGrid}>
        <label className={styles.label}>Name</label>
        <input
          className={styles.input}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          placeholder="e.g. AGM 2026 Step 1"
        />

        <label className={styles.label}>Subject</label>
        <div>
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: '#666', marginBottom: 6,
            }}
          >
            <input
              type="checkbox"
              checked={hasSubject}
              onChange={(e) => setHasSubject(e.target.checked)}
            />
            Has subject (uncheck for reply-mode templates that thread on
            an earlier step's subject)
          </label>
          {hasSubject && (
            <input
              className={styles.input}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Quick intro from {{Sender.FirstName}}"
              style={{ width: '100%' }}
            />
          )}
        </div>

        <label className={styles.label}>Body</label>
        <RichEmailEditor
          value={bodyHtml}
          onChange={setBodyHtml}
          sendMode="per_person"
        />

        <label className={styles.label}>Notes</label>
        <textarea
          className={styles.input}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Optional commentary for other authors. Never rendered."
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <div className={styles.label}>Preview</div>
        <EmailBodyPreview html={bodyHtml || '<p><em>(empty)</em></p>'} />
      </div>

      <div
        style={{
          display: 'flex', gap: 8, marginTop: 16,
          justifyContent: 'flex-end',
        }}
      >
        <button type="button" className={styles.button} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={handleSave}
          disabled={busy}
        >
          {busy ? 'Saving…' : isNew ? 'Create template' : 'Save'}
        </button>
      </div>
    </div>
  );
}
